/**
 * As datas da Central do Perito (000385), no dia de SÃO PAULO. Puro, sem import, com prova própria
 * (`tests/perito-datas.mjs`).
 *
 * Lá, toda data passava por `new Date("2026-11-03")`, que o JavaScript lê como meia-noite UTC — em
 * Brasília, 21h do dia 2 —, e o "hoje" saía de `toISOString().slice(0, 10)`, que depois das 21h já é o dia
 * seguinte. O efeito era o prazo exibido um dia antes, o alerta de "vence em 3 dias" com um dia a mais ou a
 * menos conforme a hora, e a tarefa-modelo nascendo com o prazo contado de amanhã. Aqui a data do processo
 * é um DIA (AAAA-MM-DD), e dia se compara com dia, nunca com um instante.
 */

const FUSO = "America/Sao_Paulo";
const DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

/** O dia de São Paulo de um instante (por padrão, agora), como AAAA-MM-DD. */
export function diaEmSP(instante: Date | string = new Date()): string {
  const d = typeof instante === "string" ? new Date(instante) : instante;
  if (isNaN(d.getTime())) return "";
  // en-CA escreve a data como AAAA-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: FUSO, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}

/** Hoje, em São Paulo. */
export function hoje(agora: Date = new Date()): string {
  return diaEmSP(agora);
}

/** O dia de um valor que pode ser só a data ("2026-11-03") ou um instante ("2026-11-03T14:00:00Z"). */
export function diaDe(valor: string | null | undefined): string | null {
  if (!valor) return null;
  const v = valor.trim();
  if (DIA.test(v)) return v;
  const d = diaEmSP(v);
  return d || null;
}

/** O número de dias de calendário de `de` até `ate` (os dois AAAA-MM-DD); negativo se `ate` vem antes. */
export function diasEntre(de: string, ate: string): number {
  const a = DIA.exec(de);
  const b = DIA.exec(ate);
  if (!a || !b) return NaN;
  const ta = Date.UTC(Number(a[1]), Number(a[2]) - 1, Number(a[3]));
  const tb = Date.UTC(Number(b[1]), Number(b[2]) - 1, Number(b[3]));
  return Math.round((tb - ta) / 86400000);
}

/** Quantos dias faltam (negativo: há quantos dias passou) até a data, contados de hoje em São Paulo. */
export function diasAte(valor: string | null | undefined, agora: Date = new Date()): number | null {
  const dia = diaDe(valor);
  if (!dia) return null;
  const n = diasEntre(hoje(agora), dia);
  return Number.isNaN(n) ? null : n;
}

/** O dia N dias depois de `dia` (AAAA-MM-DD). */
export function maisDias(dia: string, n: number): string {
  const m = DIA.exec(dia);
  if (!m) return dia;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n));
  return d.toISOString().slice(0, 10);
}

/** "03/11/2026" — da data (lida como DIA, sem fuso) ou do instante (no dia de São Paulo). */
export function formatarData(valor: string | null | undefined): string {
  const dia = diaDe(valor);
  if (!dia) return "—";
  const [a, m, d] = dia.split("-");
  return `${d}/${m}/${a}`;
}

/** "03/11/2026 14:05", no relógio de São Paulo. */
export function formatarDataHora(valor: string | null | undefined): string {
  if (!valor) return "—";
  const d = new Date(valor);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, dateStyle: "short", timeStyle: "short" }).format(d);
}

/** O mês de um dia, AAAA-MM. */
export function mesDe(valor: string | null | undefined): string | null {
  const dia = diaDe(valor);
  return dia ? dia.slice(0, 7) : null;
}

/** "agora", "5min atrás", "3h atrás", "2d atrás" ou a data. */
export function tempoRelativo(valor: string, agora: Date = new Date()): string {
  const ms = agora.getTime() - new Date(valor).getTime();
  if (isNaN(ms)) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return "agora";
  const min = Math.floor(s / 60);
  if (min < 60) return `${min}min atrás`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h atrás`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d atrás`;
  return formatarData(valor);
}
